import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PreSessionTab from './tabs/PreSessionTab';
import { participantService } from '../../services';
import tokenStorage from '../../utils/tokenStorage';

/**
 * Pre-Session Dashboard — thin route wrapper around PreSessionTab.
 * PreSessionTab carries the full UI (sidebar, progress bar, materials, self-assessment,
 * challenges, expectations). This page resolves sessionId/participantId and wires Back.
 */
const PreSessionDashboardPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Resolve the participant from dual storage (auto-restores from sessionStorage if needed).
  const participant = tokenStorage.getParticipantData(sessionId) || {};
  const participantId = participant.participantId || participant.id;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await participantService.getSession(sessionId);
        if (!cancelled && response?.success) setSession(response.data);
        if (!cancelled && !response?.success) setError('Session not found');
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load session');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <p className="text-neutral-600">{error || 'Pre-session not available'}</p>
      </div>
    );
  }

  // Lock editing once the session goes live/paused/completed — participants view their pre-session as history.
  const readOnly = session.status === 'live' || session.status === 'paused' || session.status === 'completed';

  return (
    <PreSessionTab
      sessionId={sessionId}
      participantId={participantId}
      readOnly={readOnly}
      sessionStatus={session.status}
      hasPostSession={!!session.postSession?.enabled}
      onBack={() => navigate(`/session/${sessionId}/lobby?view=welcome`)}
    />
  );
};

export default PreSessionDashboardPage;
